import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import './CustomerData.css';
import FacilityFilter from '../components/FacilityFilter';
import DataChart from '../components/DataChart';
import CombinedTrendChart, { getSeriesColor } from '../components/CombinedTrendChart';
import { MESSAGE_TYPE_FIELDS } from '../utils/chartBinning';
import GroupFacilitiesModal from '../components/GroupFacilitiesModal';
import { useCache } from '../hooks/useCache';
import client from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useScope } from '../contexts/ScopeContext';
import DirectionToggle from '../components/DirectionToggle';
import {
  rowMatchesFacilityScope,
  loadDirectionPreference,
  saveDirectionPreference,
  directionShortLabel,
  dataTypeHasDirection,
  anyDirectional,
  DIRECTION_OPTIONS,
} from '../utils/referralDirection';

// Module-scoped cache so the heavy multi-data-type fetch loop survives
// SPA navigation. Without this the user would re-pay the full fetch
// every time they bounce to another analysis page and back, even though
// nothing about the request changed - the per-data-type backend cache
// already returns instantly, but each call still has the React Router
// remount + N round-trips + chart redraw cost.
//
// Keyed by the request shape (start, end, sorted-types) NOT by the
// active customer / saved-filter / facility selection - facility
// filtering happens client-side after the records are already in
// memory, so the same fetched payload serves every scope variant.
//
// Bypassed by passing { force: true } from the strip's "Reload" button
// so the user always has a manual escape hatch when the underlying
// backend cache has been refreshed.
const trendDataCache = new Map();
const TREND_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
function makeTrendCacheKey(start, end, types) {
  const sortedTypes = Array.from(types).sort().join(',');
  return `${start}|${end}|${sortedTypes}`;
}

// Separate cache for the year-over-year prior-window fetches. Keyed by
// type + prior window so toggling "Compare to last year" off and back on
// (or re-selecting the same type) is instant instead of re-hitting the
// backend — the prior window lives outside the main payload's range, so it
// isn't covered by trendDataCache.
const compareDataCache = new Map();
function makeCompareCacheKey(type, start, end) {
  return `${type}|${start}|${end}`;
}

/** Inclusive day count between start and end date strings (yyyy-MM-dd). */
function countInclusiveDays(startStr, endStr) {
  if (!startStr || !endStr) return 30;
  const s = new Date(`${startStr}T12:00:00`);
  const e = new Date(`${endStr}T12:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 30;
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

/**
 * Shifts a 'yyyy-MM-dd' date back by N whole years. Feb 29 in a leap year
 * clamps to Feb 28 of the (non-leap) target year so we never roll into March.
 */
function shiftYearsBack(dateStr, years = 1) {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y - years, m - 1, d);
  if (dt.getMonth() !== m - 1) dt.setDate(0); // clamp overflow (Feb 29 -> Feb 28)
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Picks a sensible time bucket from the selected analysis window (used when binning = Auto). */
function autoBinningFromSpanDays(spanDays) {
  if (!Number.isFinite(spanDays) || spanDays < 1) return 'month';
  if (spanDays <= 45) return 'day';
  if (spanDays <= 100) return 'week';
  return 'month';
}

// "Snap to whole months" helpers. The user wants the option to clip
// fractional months off both ends of the chart so the leftmost and
// rightmost bins are guaranteed to be COMPLETE months. We never modify
// the user's selected date range — these just produce a snapped copy
// for the chart filter.

/** First day of the next month if `dateStr` isn't already day 1. */
function snapStartToFirstOfMonth(dateStr) {
  if (!dateStr) return dateStr;
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  if (d.getDate() === 1) return dateStr;
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1, 12, 0, 0);
  return next.toISOString().split('T')[0];
}

/** Last day of the previous month if `dateStr` isn't already month-end. */
function snapEndToLastOfMonth(dateStr) {
  if (!dateStr) return dateStr;
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  // Last day of the month containing d
  const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  if (d.getDate() === monthEnd) return dateStr;
  // Otherwise back up to the last day of the previous month
  const prev = new Date(d.getFullYear(), d.getMonth(), 0, 12, 0, 0);
  return prev.toISOString().split('T')[0];
}

function CustomerData() {
  const cache = useCache();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customers, uiPreferences, updateUiPreferences } = useAuth();
  // The legacy URL param is `groupId`; the new shortcut bar uses
  // `customerId` for cross-page consistency. Accept either, prefer
  // `customerId` when both are present.
  const groupIdParam = searchParams.get('customerId') || searchParams.get('groupId');
  const filterIdParam = searchParams.get('filterId');
  const facilityIdParam = searchParams.get('facilityId');
  // Global sticky scope. CustomerData keeps its own richer per-page banner
  // (with facility count + clear button), but it now mirrors any change
  // into the StickyScopeBar so the user has one consistent indicator
  // across the five analysis pages.
  const { scope: globalScope, setScope: setGlobalScope, clearScope: clearGlobalScope } = useScope();
  const autoLoadedRef = useRef(false);
  const autoLoadedFilterRef = useRef(false);
  const autoLoadedFacilityRef = useRef(false);
  const [groupContext, setGroupContext] = useState(null);
  const [filterContext, setFilterContext] = useState(null);
  const [showGroupFacilities, setShowGroupFacilities] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [facilityHierarchy, setFacilityHierarchy] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(true);
  const [facilitiesError, setFacilitiesError] = useState(null);

  // Data Trends intentionally diverges from the global tile list:
  //   - 'groups' (rpt_consolidated_group_usage, daily VChat group activity)
  //     is REPLACED by 'group-referrals' (rpt_referral_with_groups, per-row
  //     referrals routed to a queue). Group chat activity is still available
  //     on Raw Data and Insights — it's just less interesting at the
  //     per-customer trend level than referral routing.
  //   - 'faxes' is included as a primary tile and selected by default so
  //     fax volume shows up on every customer's trend grid.
  //   - 'study-details' (DICOM Studies) shows the per-study count over time,
  //     which is distinct from 'studies' (daily aggregate of study_sent +
  //     study_received routing events). Both are included so the user can
  //     see both routing volume AND unique-study volume side-by-side.
  //   - 'fax-recipients' stays VERA-only (per-user join table).
  const [dataTypes] = useState([
    { id: 'referrals', name: 'Referrals' },
    { id: 'vitalrounds', name: 'VitalRounds' },
    { id: 'messages', name: 'Messages' },
    { id: 'group-referrals', name: 'Group Referrals' },
    { id: 'logins', name: 'Logins' },
    { id: 'studies', name: 'Studies' },
    { id: 'study-details', name: 'DICOM Studies' },
    { id: 'faxes', name: 'Faxes' }
  ]);

  const [dateRange, setDateRange] = useState('last30days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // "Snap to whole months" — when on, both the data fetch and the chart
  // bins are clipped to complete calendar months on each end. Useful for
  // month-over-month comparisons where a half-finished trailing month
  // would distort the visual. Default off (preserves existing
  // behavior); persisted to localStorage so power users don't have to
  // re-enable it on every visit.
  const [snapToWholeMonths, setSnapToWholeMonths] = useState(() => {
    try { return localStorage.getItem('cd-snap-to-whole-months') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cd-snap-to-whole-months', snapToWholeMonths ? '1' : '0'); }
    catch { /* ignore quota / privacy-mode storage errors */ }
  }, [snapToWholeMonths]);
  // All eight tiles are checked by default so first-visit users see the
  // full trend picture, including the V2.1 group-referrals + faxes +
  // study-details signals that are unique to this page.
  const [selectedDataTypes, setSelectedDataTypes] = useState(new Set(['referrals', 'vitalrounds', 'messages', 'group-referrals', 'logins', 'studies', 'study-details', 'faxes']));

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [rawLoadedData, setRawLoadedData] = useState({});
  const [loadingTypes, setLoadingTypes] = useState(() => new Set());
  const [dataLoadComplete, setDataLoadComplete] = useState(false);

  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);

  // Sending-facility filter (applies to Referrals + VitalRounds only; those are the
  // only data types that carry the sender's facility). Other data types ignore it.
  // "Sending" matches sending_facility_id — the canonical name DataCacheService
  // writes after rewriting VitalEngine's misleading API field-name prefixes.
  // See REFERRAL-FIELD-NAMING.md.
  const [selectedSendingFacilityIds, setSelectedSendingFacilityIds] = useState(new Set());
  const [sendingSearchQuery, setSendingSearchQuery] = useState('');

  const [showRaw, setShowRaw] = useState(false);
  const [rawEphemeralData, setRawEphemeralData] = useState({});
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [rawProgress, setRawProgress] = useState(null);
  const [rawErrors, setRawErrors] = useState([]);

  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState('');
  const [expandedChart, setExpandedChart] = useState(null);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  // Inbound / outbound / either direction filter for the receiving
  // facility scope. Persisted across reloads. Applies only to data
  // types that carry both sender and receiver facility IDs (referrals
  // / vitalrounds / group-referrals); non-directional types ignore it.
  const [direction, setDirectionState] = useState(loadDirectionPreference);
  const setDirection = (v) => {
    setDirectionState(v);
    saveDirectionPreference(v);
  };
  /** 'auto' picks day / week / month from the current date range; otherwise fixed bucket. */
  const [chartBinningMode, setChartBinningMode] = useState('auto');

  // Chart layout: 'grid' = one tile per data type; 'single' = one chart with
  // a selector to overlay one or more data types. Persisted PER USER via
  // server preferences (uiPreferences). First-time default is 'single'.
  const [chartViewMode, setChartViewMode] = useState('single');
  // Which data types are overlaid on the single/combined chart. Defaults to
  // just referrals (the headline metric) so it paints the instant referrals
  // data lands; the user toggles others on/off.
  const [overlayTypes, setOverlayTypes] = useState(() => new Set(['referrals']));
  // Overlay y-axis mode: false = each series auto-scaled on its own hidden
  // axis (compare trend shape); true = one shared axis (compare magnitude).
  const [sharedScale, setSharedScale] = useState(false);
  // Time bucket for the single/combined chart. null = follow the page's
  // auto/selected binning; a concrete value = user override on this chart.
  const [singleBinningOverride, setSingleBinningOverride] = useState(null);
  // Which message channel the single view's "Messages" series isolates.
  // 'combined' = the rolled-up total (matches the tiled combined view); a
  // field name isolates that one VitalEngine channel.
  const [singleMessageType, setSingleMessageType] = useState('combined');

  // Year-over-year comparison (single view only). When on, the overlay is
  // restricted to ONE data type and the chart also plots that type for the
  // same window one year earlier. compareData holds the fetched prior-year
  // records for the active type + window.
  const [compareYoY, setCompareYoY] = useState(false);
  const [compareData, setCompareData] = useState(null); // { type, records, start, end }
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);

  // Hydrate the per-user Data Trends preferences once they arrive from /me.
  // Runs only until the first successful hydration so it never clobbers an
  // in-session change the user just made.
  const trendsPrefsHydratedRef = useRef(false);
  useEffect(() => {
    if (trendsPrefsHydratedRef.current) return;
    if (!uiPreferences) return;
    const v = uiPreferences.dataTrendsView;
    if (v === 'grid' || v === 'single') setChartViewMode(v);
    const series = uiPreferences.dataTrendsSeries;
    if (Array.isArray(series)) {
      const valid = series.filter(id => dataTypes.some(d => d.id === id));
      if (valid.length > 0) setOverlayTypes(new Set(valid));
    }
    if (typeof uiPreferences.dataTrendsSharedScale === 'boolean') {
      setSharedScale(uiPreferences.dataTrendsSharedScale);
    }
    trendsPrefsHydratedRef.current = true;
  }, [uiPreferences, dataTypes]);

  const changeChartViewMode = (mode) => {
    setChartViewMode(mode);
    updateUiPreferences({ dataTrendsView: mode });
  };
  // Toggle a data type in the overlay; always keep at least one series so the
  // chart never goes blank. While year-over-year compare is on the selector
  // acts as a radio (exactly one type) since comparing multiple types against
  // their own prior years at once gets unreadable.
  const toggleOverlayType = (id) => {
    if (compareYoY) {
      const next = new Set([id]);
      setOverlayTypes(next);
      updateUiPreferences({ dataTrendsSeries: [id] });
      return;
    }
    const next = new Set(overlayTypes);
    if (next.has(id)) {
      if (next.size > 1) next.delete(id);
    } else {
      next.add(id);
    }
    setOverlayTypes(next);
    updateUiPreferences({ dataTrendsSeries: Array.from(next) });
  };
  const changeSharedScale = (val) => {
    setSharedScale(val);
    updateUiPreferences({ dataTrendsSharedScale: val });
  };
  // Turning compare ON collapses the overlay to a single type (the first one
  // currently selected) so the chart stays legible.
  const toggleCompareYoY = (val) => {
    setCompareYoY(val);
    if (val && overlayTypes.size > 1) {
      const first = Array.from(overlayTypes)[0];
      const next = new Set([first]);
      setOverlayTypes(next);
      updateUiPreferences({ dataTrendsSeries: [first] });
    }
  };

  useEffect(() => {
    loadFacilities();
    loadSavedFilters();
  }, []);

  useEffect(() => {
    if (!showRaw) {
      setRawEphemeralData({});
    }
  }, [showRaw]);

  // Auto-load a year of data filtered by a customer, when ?groupId= is in the URL.
  // Runs once per groupId change; state is still fully editable afterwards.
  useEffect(() => {
    if (!groupIdParam) {
      autoLoadedRef.current = false;
      setGroupContext(null);
      return;
    }
    if (autoLoadedRef.current === groupIdParam) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await client.get(`/api/customers/${groupIdParam}/resolved-facilities`);
        if (cancelled) return;

        const ids = (res.data?.facilityIds || []).map(id => id.toString());
        const resolvedName =
          customers?.find(ag => String(ag.id) === String(groupIdParam))?.name
          || res.data?.name
          || res.data?.groupName
          || null;

        setGroupContext({ id: groupIdParam, name: resolvedName, facilityCount: ids.length });
        setSelectedFacilityIds(new Set(ids));
        setActiveFilterId('');
        setDateRange('last365days');
        const allTypes = new Set(['referrals', 'vitalrounds', 'messages', 'group-referrals', 'logins', 'studies', 'study-details', 'faxes']);
        setSelectedDataTypes(allTypes);

        autoLoadedRef.current = groupIdParam;

        const end = new Date();
        const start = new Date();
        start.setFullYear(end.getFullYear() - 1);
        const startStr = start.toISOString().split('T')[0];
        const endStr = end.toISOString().split('T')[0];

        await loadAllData({ dataTypes: allTypes, start: startStr, end: endStr });
      } catch {
        if (!cancelled) {
          setGroupContext({ id: groupIdParam, name: null, facilityCount: 0, error: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIdParam]);

  const groupDisplayName = useMemo(() => {
    if (!groupContext) return null;
    const liveMatch = customers?.find(
      ag => String(ag.id) === String(groupContext.id)
    )?.name;
    return liveMatch || groupContext.name || 'Customer';
  }, [groupContext, customers]);

  const clearGroupContext = () => {
    setGroupContext(null);
    autoLoadedRef.current = false;
    setSelectedFacilityIds(new Set());
    const next = new URLSearchParams(searchParams);
    next.delete('groupId');
    next.delete('customerId');
    setSearchParams(next, { replace: true });
    clearGlobalScope();
  };

  // Auto-load a year of data filtered by a saved filter, when ?filterId= is in the URL.
  useEffect(() => {
    if (!filterIdParam) {
      autoLoadedFilterRef.current = false;
      setFilterContext(null);
      return;
    }
    if (autoLoadedFilterRef.current === filterIdParam) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await cache.getSavedFilters();
        if (cancelled) return;

        if (res?.filters) setSavedFilters(res.filters);
        const filter = (res?.filters || []).find(f => String(f.id) === String(filterIdParam));
        if (!filter) {
          setFilterContext({ id: filterIdParam, name: null, facilityCount: 0, error: true });
          return;
        }

        const ids = (filter.facilityIds || []).map(id => id.toString());
        setFilterContext({ id: filterIdParam, name: filter.name, facilityCount: ids.length });
        setSelectedFacilityIds(new Set(ids));
        setActiveFilterId(String(filter.id));
        setDateRange('last365days');
        const allTypes = new Set(['referrals', 'vitalrounds', 'messages', 'group-referrals', 'logins', 'studies', 'study-details', 'faxes']);
        setSelectedDataTypes(allTypes);

        autoLoadedFilterRef.current = filterIdParam;

        const end = new Date();
        const start = new Date();
        start.setFullYear(end.getFullYear() - 1);
        const startStr = start.toISOString().split('T')[0];
        const endStr = end.toISOString().split('T')[0];

        await loadAllData({ dataTypes: allTypes, start: startStr, end: endStr });
      } catch {
        if (!cancelled) {
          setFilterContext({ id: filterIdParam, name: null, facilityCount: 0, error: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIdParam]);

  // Auto-load a year of data scoped to a single facility, when ?facilityId=
  // is in the URL. The shortcut bar on the All Facilities page produces
  // these. Always uses the canonical integer-string VitalEngine facility ID.
  useEffect(() => {
    if (!facilityIdParam) {
      autoLoadedFacilityRef.current = false;
      return;
    }
    if (autoLoadedFacilityRef.current === facilityIdParam) return;

    let cancelled = false;
    (async () => {
      try {
        const ids = new Set([String(facilityIdParam)]);
        if (cancelled) return;
        setSelectedFacilityIds(ids);
        setActiveFilterId('');
        setDateRange('last365days');
        const allTypes = new Set(['referrals', 'vitalrounds', 'messages', 'group-referrals', 'logins', 'studies', 'study-details', 'faxes']);
        setSelectedDataTypes(allTypes);

        autoLoadedFacilityRef.current = facilityIdParam;

        const end = new Date();
        const start = new Date();
        start.setFullYear(end.getFullYear() - 1);
        const startStr = start.toISOString().split('T')[0];
        const endStr = end.toISOString().split('T')[0];

        await loadAllData({ dataTypes: allTypes, start: startStr, end: endStr });
      } catch { /* swallow — UI shows empty state on its own */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityIdParam]);

  const clearFilterContext = () => {
    setFilterContext(null);
    autoLoadedFilterRef.current = false;
    setSelectedFacilityIds(new Set());
    setActiveFilterId('');
    const next = new URLSearchParams(searchParams);
    next.delete('filterId');
    setSearchParams(next, { replace: true });
    clearGlobalScope();
  };

  // ---- Sticky scope sync -------------------------------------------------
  // Mirror this page's local groupContext / filterContext / single-facility
  // selection into the global StickyScopeBar, and react to external clears
  // (sticky bar X, or another analysis page mutating scope) by tearing down
  // the matching local context.
  //
  // Order of precedence whenever they conflict:
  //   1. URL params win on mount / navigation. The chip immediately mirrors
  //      ?customerId / ?filterId / ?facilityId so clicking a card on My
  //      Customers updates the chip from the *previous* customer to the
  //      newly clicked one without waiting for the resolved-facilities
  //      fetch.
  //   2. Local groupContext / filterContext win once their async fetches
  //      complete (they upgrade the chip's label from "Customer #9" to the
  //      real name).
  //   3. globalScope drives behaviour only when the URL has no scope param
  //      (e.g. user got here via sidebar nav from another analysis page).

  // 1. URL params → globalScope (synchronous, runs as soon as the URL
  //    changes — before the legacy auto-load effects fire). Also runs once
  //    customers / savedFilters arrive so the placeholder label upgrades.
  useEffect(() => {
    if (groupIdParam) {
      const c = customers?.find(x => String(x.id) === String(groupIdParam));
      setGlobalScope({
        kind: 'customer',
        id: groupIdParam,
        label: c?.name || `Customer #${groupIdParam}`,
      });
    } else if (filterIdParam) {
      const sf = savedFilters?.find(x => String(x.id) === String(filterIdParam));
      setGlobalScope({
        kind: 'filter',
        id: filterIdParam,
        label: sf?.name || `Filter #${filterIdParam}`,
      });
    } else if (facilityIdParam) {
      setGlobalScope({
        kind: 'facility',
        id: facilityIdParam,
        label: `Facility ${facilityIdParam}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIdParam, filterIdParam, facilityIdParam, customers, savedFilters]);

  // 2. Local context → globalScope label upgrade. groupContext / filterContext
  //    arrive after their fetches and have richer names than the URL alone.
  useEffect(() => {
    if (groupContext && !groupContext.error) {
      setGlobalScope({
        kind: 'customer',
        id: groupContext.id,
        label: groupDisplayName || groupContext.name || `Customer #${groupContext.id}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupContext, groupDisplayName]);

  useEffect(() => {
    if (!groupContext && filterContext && !filterContext.error) {
      setGlobalScope({
        kind: 'filter',
        id: filterContext.id,
        label: filterContext.name || `Filter #${filterContext.id}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterContext, groupContext]);

  // 3. globalScope → local (URL): only when the user arrived without a URL
  //    param but the chip was sticky from another page. Write the URL so
  //    the legacy auto-load effects pick it up. Skip when URL already has
  //    a scope param so we never clobber a fresh navigation.
  useEffect(() => {
    if (!globalScope) {
      if (groupContext) clearGroupContext();
      if (filterContext) clearFilterContext();
      if (facilityIdParam) {
        const next = new URLSearchParams(searchParams);
        next.delete('facilityId');
        setSearchParams(next, { replace: true });
        autoLoadedFacilityRef.current = false;
        setSelectedFacilityIds(new Set());
      }
      return;
    }

    if (groupIdParam || filterIdParam || facilityIdParam) return; // URL wins

    if (groupContext && globalScope.kind === 'customer' && String(groupContext.id) === String(globalScope.id)) return;
    if (filterContext && globalScope.kind === 'filter' && String(filterContext.id) === String(globalScope.id)) return;

    const next = new URLSearchParams(searchParams);
    if (globalScope.kind === 'customer') next.set('customerId', globalScope.id);
    else if (globalScope.kind === 'filter') next.set('filterId', globalScope.id);
    else if (globalScope.kind === 'facility') next.set('facilityId', globalScope.id);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalScope, groupIdParam, filterIdParam, facilityIdParam]);

  const loadFacilities = async () => {
    try {
      setLoadingFacilities(true);
      const [flatResponse, hierarchyResponse] = await Promise.all([
        cache.getCachedFacilities(),
        cache.getCachedFacilityHierarchy()
      ]);

      if (flatResponse?.success) {
        setFacilities(flatResponse.facilities || []);
      }
      if (hierarchyResponse?.success) {
        setFacilityHierarchy(hierarchyResponse.hierarchy || []);
      }
      setFacilitiesError(null);
    } catch {
      setFacilitiesError('No cached facilities found. Please refresh facilities from the Analytics page first.');
    } finally {
      setLoadingFacilities(false);
    }
  };

  const loadSavedFilters = async () => {
    try {
      const response = await cache.getSavedFilters();
      if (response?.filters) {
        setSavedFilters(response.filters);
      }
    } catch {
      // Filters not available yet
    }
  };

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    if (!filterId) {
      setSelectedFacilityIds(new Set());
      return;
    }
    const filter = savedFilters.find(f => f.id === parseInt(filterId));
    if (filter?.facilityIds) {
      setSelectedFacilityIds(new Set(filter.facilityIds));
    }
  };

  // The "what is the user analyzing right now?" question has four possible
  // sources that all converge on selectedFacilityIds:
  //   - groupContext     (URL ?customerId=N — customer scope)
  //   - filterContext    (URL ?filterId=N   — saved filter scope)
  //   - activeFilterId   (in-page Facility Filter dropdown)
  //   - selectedFacilityIds with activeFilterId='' (Custom Filter modal)
  //   - none of the above → "All VitalEngine Facilities"
  //
  // The title, the green "Viewing" bar, and the filter strip's FACILITIES
  // column all need to read the same answer or they get out of sync (e.g.
  // dropdown-picked filter rendering "All VitalEngine Facilities" in the
  // title because we only checked group/filterContext).
  const currentScope = useMemo(() => {
    if (groupContext) {
      return {
        kind: 'customer',
        name: groupDisplayName,
        viewingLabel: 'Viewing:',
        facilityCount: groupContext.facilityCount,
        bannerClass: 'customer-data-group-banner',
        clear: () => clearGroupContext(),
        clearLabel: 'Clear group filter',
        clearTitle: 'View the facilities in this group',
      };
    }
    if (filterContext && !filterContext.error) {
      return {
        kind: 'filter-url',
        name: filterContext.name,
        viewingLabel: 'Viewing saved filter:',
        facilityCount: filterContext.facilityCount,
        bannerClass: 'customer-data-group-banner customer-data-filter-banner',
        clear: () => clearFilterContext(),
        clearLabel: 'Clear filter',
        clearTitle: 'View the facilities in this filter',
      };
    }
    if (activeFilterId) {
      const sf = savedFilters.find(s => String(s.id) === String(activeFilterId));
      if (sf) {
        return {
          kind: 'filter-dropdown',
          name: sf.name,
          viewingLabel: 'Viewing saved filter:',
          facilityCount: selectedFacilityIds.size,
          bannerClass: 'customer-data-group-banner customer-data-filter-banner',
          clear: () => { setActiveFilterId(''); setSelectedFacilityIds(new Set()); },
          clearLabel: 'Clear filter',
          clearTitle: 'View the facilities in this filter',
        };
      }
    }
    if (selectedFacilityIds.size > 0) {
      return {
        kind: 'custom',
        name: `Custom selection (${selectedFacilityIds.size} facilit${selectedFacilityIds.size === 1 ? 'y' : 'ies'})`,
        viewingLabel: 'Viewing custom selection:',
        facilityCount: selectedFacilityIds.size,
        bannerClass: 'customer-data-group-banner customer-data-filter-banner',
        clear: () => { setSelectedFacilityIds(new Set()); setActiveFilterId(''); },
        clearLabel: 'Clear selection',
        clearTitle: 'View the selected facilities',
      };
    }
    return {
      kind: 'none',
      name: 'All VitalEngine Facilities',
      viewingLabel: null,
      facilityCount: 0,
      bannerClass: null,
      clear: null,
      clearLabel: null,
      clearTitle: null,
    };
    // clearGroupContext / clearFilterContext are stable closures — fine to
    // omit from deps since they don't capture changing values that affect
    // currentScope. eslint will lint that for us if it ever changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupContext, groupDisplayName, filterContext, activeFilterId, savedFilters, selectedFacilityIds]);

  const dateRangeBounds = useMemo(() => {
    const end = new Date();
    let start = new Date();
    switch (dateRange) {
      case 'last7days': start.setDate(end.getDate() - 7); break;
      case 'last30days': start.setDate(end.getDate() - 30); break;
      case 'last90days': start.setDate(end.getDate() - 90); break;
      case 'last6months': start.setDate(end.getDate() - 180); break;
      case 'last9months': start.setDate(end.getDate() - 270); break;
      case 'last365days': start.setFullYear(end.getFullYear() - 1); break;
      case 'last18months': start.setMonth(end.getMonth() - 18); break;
      case 'last2years': start.setFullYear(end.getFullYear() - 2); break;
      case 'custom': return { start: startDate, end: endDate };
      default: start.setDate(end.getDate() - 30);
    }
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, [dateRange, startDate, endDate]);

  // Snapped copy of the bounds for "whole months only" mode. If
  // snapping leaves an empty (or inverted) range — e.g. user picked
  // "Last 7 days" mid-month so the snap clips both ends past each
  // other — we fall back to the un-snapped bounds and surface a hint
  // to the user.
  const effectiveDateRangeBounds = useMemo(() => {
    if (!snapToWholeMonths) return dateRangeBounds;
    const snappedStart = snapStartToFirstOfMonth(dateRangeBounds.start);
    const snappedEnd   = snapEndToLastOfMonth(dateRangeBounds.end);
    if (!snappedStart || !snappedEnd || snappedStart > snappedEnd) {
      return dateRangeBounds;
    }
    return { start: snappedStart, end: snappedEnd };
  }, [snapToWholeMonths, dateRangeBounds]);

  const snapApplied = (
    snapToWholeMonths &&
    (effectiveDateRangeBounds.start !== dateRangeBounds.start ||
     effectiveDateRangeBounds.end   !== dateRangeBounds.end)
  );
  const snapEmpty = (
    snapToWholeMonths &&
    effectiveDateRangeBounds.start === dateRangeBounds.start &&
    effectiveDateRangeBounds.end   === dateRangeBounds.end &&
    // Only treat as "empty" when the window doesn't naturally already
    // sit on month boundaries (in which case nothing got snapped).
    (snapStartToFirstOfMonth(dateRangeBounds.start) !== dateRangeBounds.start ||
     snapEndToLastOfMonth(dateRangeBounds.end) !== dateRangeBounds.end)
  );

  const dateRangeSpanDays = useMemo(
    () => countInclusiveDays(effectiveDateRangeBounds.start, effectiveDateRangeBounds.end),
    [effectiveDateRangeBounds]
  );

  const effectiveChartBinning = useMemo(() => {
    if (chartBinningMode !== 'auto') return chartBinningMode;
    return autoBinningFromSpanDays(dateRangeSpanDays);
  }, [chartBinningMode, dateRangeSpanDays]);

  // The single/combined chart follows the page binning unless the user picks
  // an explicit Group by on the chart itself.
  const singleBinning = singleBinningOverride || effectiveChartBinning;

  // The data type being compared year-over-year (single view, compare on).
  const compareType = compareYoY ? Array.from(overlayTypes)[0] : null;
  // Prior-year window = current effective window shifted back one year.
  const priorWindow = useMemo(() => ({
    start: shiftYearsBack(effectiveDateRangeBounds.start, 1),
    end: shiftYearsBack(effectiveDateRangeBounds.end, 1),
  }), [effectiveDateRangeBounds]);

  // Fetch the prior-year records for the active compare type/window. Kept
  // separate from loadAllData because the prior window is outside the
  // user's selected range, so it isn't part of the main payload.
  useEffect(() => {
    if (!compareYoY || !compareType) {
      setCompareData(null);
      setCompareError(null);
      setCompareLoading(false);
      return;
    }
    // Serve from the client cache when fresh so re-checking the box (or
    // re-picking the same type/window) doesn't re-churn the backend.
    const cacheKey = makeCompareCacheKey(compareType, priorWindow.start, priorWindow.end);
    const cached = compareDataCache.get(cacheKey);
    if (cached && (Date.now() - cached.savedAt) < TREND_CACHE_TTL_MS) {
      setCompareData(cached.data);
      setCompareLoading(false);
      setCompareError(null);
      return;
    }

    let cancelled = false;
    setCompareLoading(true);
    setCompareError(null);
    cache.getCachedData(compareType, priorWindow.start, priorWindow.end)
      .then((resp) => {
        if (cancelled) return;
        if (resp && resp.success) {
          const data = {
            type: compareType,
            records: resp.records || [],
            start: priorWindow.start,
            end: priorWindow.end,
          };
          compareDataCache.set(cacheKey, { data, savedAt: Date.now() });
          setCompareData(data);
        } else {
          setCompareError('Failed to load prior-year data');
          setCompareData(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setCompareError(err?.message || 'Failed to load prior-year data');
        setCompareData(null);
      })
      .finally(() => {
        if (!cancelled) setCompareLoading(false);
      });
    return () => { cancelled = true; };
  }, [compareYoY, compareType, priorWindow, cache]);

  const chartBinningPretty = useMemo(() => {
    const label = { day: 'Day', week: 'Week', month: 'Month' };
    return label[effectiveChartBinning] || effectiveChartBinning;
  }, [effectiveChartBinning]);

  const chartBinningSummary = useMemo(() => {
    if (chartBinningMode === 'auto') {
      return `Auto (${chartBinningPretty})`;
    }
    return chartBinningPretty;
  }, [chartBinningMode, chartBinningPretty]);

  const calculateDateRange = () => effectiveDateRangeBounds;

  const toggleDataType = (dataTypeValue) => {
    setSelectedDataTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataTypeValue)) newSet.delete(dataTypeValue);
      else newSet.add(dataTypeValue);
      return newSet;
    });
  };

  const loadAllData = async (overrides) => {
    // Always load every data type so both views are fully backed: the grid
    // shows the user's checked tiles, and the single/overlay view can offer
    // ALL data types without a re-fetch. (Per product intent: "still load
    // all of the data to support the different views".) The per-data-type
    // backend cache makes the extra types cheap on repeat loads.
    const types = overrides?.dataTypes
      ? Array.from(overrides.dataTypes)
      : dataTypes.map(d => d.id);
    if (types.length === 0) {
      alert('Please select at least one data type');
      return;
    }

    // Fetch referrals first so the single-chart view (which defaults to
    // referrals) and the first grid tile paint as early as possible. The
    // remaining types still fetch concurrently right after, so this only
    // nudges ordering — it doesn't serialize the loop.
    types.sort((a, b) => {
      if (a === 'referrals') return -1;
      if (b === 'referrals') return 1;
      return 0;
    });

    const { start, end } = overrides?.start && overrides?.end
      ? { start: overrides.start, end: overrides.end }
      : calculateDateRange();

    // Cache check - skip the entire fetch loop when we have a fresh
    // result for this exact request shape. Bypassed by the strip's
    // Reload button (overrides.force === true).
    const cacheKey = makeTrendCacheKey(start, end, types);
    const cached = trendDataCache.get(cacheKey);
    const cacheFresh = cached && (Date.now() - cached.savedAt) < TREND_CACHE_TTL_MS;
    if (!overrides?.force && cacheFresh) {
      setRawLoadedData(cached.data);
      setShowRaw(false);
      setRawEphemeralData({});
      setDataLoadComplete(true);
      setFiltersCollapsed(true);
      setIsLoadingData(false);
      setLoadingTypes(new Set());
      return;
    }

    setIsLoadingData(true);
    setRawLoadedData({});
    setDataLoadComplete(false);
    setShowRaw(false);
    setRawEphemeralData({});
    setLoadingTypes(new Set(types));

    // Accumulate alongside the incremental setRawLoadedData calls so
    // the final cache entry reflects every data type the loop fetched
    // (including the ones that errored - we cache the error marker too
    // so a re-mount doesn't re-trigger broken endpoints).
    const accumulated = {};

    const fetchOne = async (dataType) => {
      try {
        const response = await cache.getCachedData(dataType, start, end);
        if (response.success) {
          const records = response.records || [];
          accumulated[dataType] = records;
          setRawLoadedData(prev => ({ ...prev, [dataType]: records }));
        } else {
          throw new Error('Failed to load data');
        }
      } catch (error) {
        const errMarker = { error: true, message: error.message };
        accumulated[dataType] = errMarker;
        setRawLoadedData(prev => ({ ...prev, [dataType]: errMarker }));
      } finally {
        setLoadingTypes(prev => {
          const next = new Set(prev);
          next.delete(dataType);
          return next;
        });
      }
    };

    // Bounded fan-out instead of Promise.all over every type. The backend
    // runs on a single vCPU, so ~9 simultaneous 10-60 MB responses gained
    // nothing in wall time but tripled peak memory on both ends (the Sep 9
    // 2026 prod OOM was exactly this page loading a 12-month window).
    // Referrals is already sorted first so the default chart still paints
    // early; the rest drain through the pool as slots free up.
    const CONCURRENCY = 3;
    const queue = [...types];
    const worker = async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        // eslint-disable-next-line no-await-in-loop
        await fetchOne(next);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, types.length) }, worker),
    );

    setIsLoadingData(false);
    setDataLoadComplete(true);
    setFiltersCollapsed(true);
    setLoadingTypes(new Set());

    trendDataCache.set(cacheKey, { data: accumulated, savedAt: Date.now() });
  };

  const handleRawToggle = async () => {
    if (showRaw) {
      setShowRaw(false);
      setRawProgress(null);
      setRawErrors([]);
      return;
    }

    if (!dataLoadComplete || selectedDataTypes.size === 0) return;

    setLoadingRaw(true);
    setRawErrors([]);
    const { start, end } = calculateDateRange();
    const ephemeral = {};
    const errors = [];
    const types = Array.from(selectedDataTypes);

    for (let i = 0; i < types.length; i++) {
      const dataType = types[i];
      setRawProgress({ current: i + 1, total: types.length, type: getDataTypeName(dataType) });
      try {
        const response = await cache.getRawData(dataType, start, end);
        if (response.success) {
          ephemeral[dataType] = response.records || [];
        } else {
          ephemeral[dataType] = { error: true, message: response.error || 'Unknown error' };
          errors.push({ type: dataType, name: getDataTypeName(dataType), message: response.error || 'Unknown error' });
        }
      } catch (error) {
        const msg = error.response?.data?.error || error.message || 'Request failed';
        ephemeral[dataType] = { error: true, message: msg };
        errors.push({ type: dataType, name: getDataTypeName(dataType), message: msg });
      }
    }

    setRawEphemeralData(ephemeral);
    setRawErrors(errors);
    setShowRaw(true);
    setLoadingRaw(false);
    setRawProgress(null);
  };

  const facilityNamesMap = useMemo(() => {
    const map = new Map();
    facilities.forEach(facility => {
      if (facility.vitalEngine_Facility_ID__c) {
        const raw = facility.vitalEngine_Facility_ID__c.toString();
        map.set(raw, facility.name);
        const normalized = Math.round(parseFloat(raw)).toString();
        if (normalized !== raw) map.set(normalized, facility.name);
      }
    });
    return map;
  }, [facilities]);

  const sendingSearchMatches = useMemo(() => {
    const q = sendingSearchQuery.trim().toLowerCase();
    if (!q) return [];
    const results = [];
    for (const f of facilities) {
      if (!f.vitalEngine_Facility_ID__c) continue;
      const id = Math.round(parseFloat(f.vitalEngine_Facility_ID__c.toString())).toString();
      if (selectedSendingFacilityIds.has(id)) continue;
      const name = f.name || '';
      const city = f.billingCity || '';
      const state = f.billingState || '';
      const hay = `${name} ${city} ${state}`.toLowerCase();
      if (hay.includes(q)) {
        const sub = [city, state].filter(Boolean).join(', ');
        results.push({ id, name, sub });
        if (results.length >= 30) break;
      }
    }
    return results;
  }, [sendingSearchQuery, facilities, selectedSendingFacilityIds]);

  const addSendingFacility = (id) => {
    setSelectedSendingFacilityIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setSendingSearchQuery('');
  };

  const removeSendingFacility = (id) => {
    setSelectedSendingFacilityIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const activeData = useMemo(() => {
    if (!showRaw || Object.keys(rawEphemeralData).length === 0) return rawLoadedData;
    const merged = { ...rawLoadedData };
    Object.entries(rawEphemeralData).forEach(([type, records]) => {
      if (Array.isArray(records)) merged[type] = records;
    });
    return merged;
  }, [showRaw, rawEphemeralData, rawLoadedData]);

  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    Object.values(activeData).forEach(records => {
      if (Array.isArray(records)) {
        records.forEach(record => {
          const recordData = record.data || record.Data;
          const facilityId = recordData?.receiving_facility_id || recordData?.facility_id;
          if (facilityId) {
            ids.add(Math.round(parseFloat(facilityId.toString())).toString());
          }
        });
      }
    });
    return ids;
  }, [activeData]);

  const availableFacilityIds = dataFacilityIds;

  const filteredData = useMemo(() => {
    if (Object.keys(activeData).length === 0) return {};
    const hasReceiving = selectedFacilityIds.size > 0;
    const hasSending = selectedSendingFacilityIds.size > 0;
    // If snap is on AND the snapped window differs from the raw window,
    // clip records by their timestamp regardless of facility filters —
    // otherwise toggling snap after loading wouldn't update the chart.
    const snapClipActive = snapToWholeMonths && snapApplied;
    const snapStart = snapClipActive ? effectiveDateRangeBounds.start : null;
    const snapEnd   = snapClipActive ? effectiveDateRangeBounds.end   : null;
    if (!hasReceiving && !hasSending && !snapClipActive) return activeData;

    const inSnapWindow = (record) => {
      if (!snapClipActive) return true;
      // Mirror DataChart's date-extraction rule so we keep/drop the
      // exact records the chart would render.
      const recordData = record.data || record.Data;
      const ts = record.timestamp || recordData?.created_date;
      if (!ts) return true; // can't classify — let it through
      const day = String(ts).slice(0, 10); // 'YYYY-MM-DD'
      return day >= snapStart && day <= snapEnd;
    };

    const filtered = {};
    Object.entries(activeData).forEach(([dataType, records]) => {
      if (!Array.isArray(records)) {
        filtered[dataType] = records;
        return;
      }
      const applySending = hasSending && (dataType === 'referrals' || dataType === 'vitalrounds');

      filtered[dataType] = records.filter(record => {
        const recordData = record.data || record.Data;

        if (hasReceiving) {
          // For directional data types the toggle decides which side(s)
          // are matched; for single-facility types the helper falls
          // back to facility_id and the toggle is a no-op.
          if (!rowMatchesFacilityScope(recordData, dataType, selectedFacilityIds, direction)) {
            return false;
          }
        }

        if (applySending) {
          // The Sending facility filter is a separate, more specific
          // dimension than the toggle: it lets the user pin a specific
          // set of sender facilities for referrals / vitalrounds while
          // still using the receiving-side scope above. The two stack.
          // See REFERRAL-FIELD-NAMING.md.
          const sid = recordData?.sending_facility_id;
          if (sid == null) return false;
          const sidStr = sid.toString();
          const sidInt = Math.round(parseFloat(sidStr)).toString();
          if (!(selectedSendingFacilityIds.has(sidStr) || selectedSendingFacilityIds.has(sidInt))) return false;
        }

        if (!inSnapWindow(record)) return false;

        return true;
      });
    });
    return filtered;
  }, [activeData, selectedFacilityIds, selectedSendingFacilityIds, dataLoadComplete, snapToWholeMonths, snapApplied, effectiveDateRangeBounds, direction]);

  // Apply the same receiving/sending facility scope to the prior-year records
  // as filteredData does for the current window, so the comparison is
  // apples-to-apples. Snap clipping isn't needed: the prior window was fetched
  // already bounded to the (snapped) effective range shifted back a year.
  const comparisonSeries = useMemo(() => {
    if (!compareData || !Array.isArray(compareData.records)) return null;
    const { type, records, start, end } = compareData;
    const hasReceiving = selectedFacilityIds.size > 0;
    const hasSending = selectedSendingFacilityIds.size > 0;
    const applySending = hasSending && (type === 'referrals' || type === 'vitalrounds');

    const filtered = (!hasReceiving && !hasSending)
      ? records
      : records.filter(record => {
          const recordData = record.data || record.Data;
          if (hasReceiving && !rowMatchesFacilityScope(recordData, type, selectedFacilityIds, direction)) {
            return false;
          }
          if (applySending) {
            const sid = recordData?.sending_facility_id;
            if (sid == null) return false;
            const sidStr = sid.toString();
            const sidInt = Math.round(parseFloat(sidStr)).toString();
            if (!(selectedSendingFacilityIds.has(sidStr) || selectedSendingFacilityIds.has(sidInt))) return false;
          }
          return true;
        });

    return {
      records: filtered,
      windowStart: start,
      windowEnd: end,
      label: 'same period last year',
    };
  }, [compareData, selectedFacilityIds, selectedSendingFacilityIds, direction]);

  const getDataTypeName = (dataTypeId) => {
    const dt = dataTypes.find(d => d.id === dataTypeId);
    return dt ? dt.name : dataTypeId;
  };

  // Pretty-print "YYYY-MM-DD" as "M/D/YYYY" (no leading zeros) for the
  // labels — matches how the user requested the format (e.g.
  // "6/1/2025 - 5/31/2026").
  const formatDateShort = (yyyymmdd) => {
    if (!yyyymmdd) return '';
    const [y, m, d] = yyyymmdd.split('-').map(Number);
    if (!y || !m || !d) return yyyymmdd;
    return `${m}/${d}/${y}`;
  };

  const dateRangeLabel = useMemo(() => {
    let presetName;
    switch (dateRange) {
      case 'last7days':    presetName = 'Last 7 days';   break;
      case 'last30days':   presetName = 'Last 30 days';  break;
      case 'last90days':   presetName = 'Last 90 days';  break;
      case 'last6months':  presetName = 'Last 6 months'; break;
      case 'last9months':  presetName = 'Last 9 months'; break;
      case 'last365days':  presetName = 'Last year';     break;
      case 'last18months': presetName = 'Last 18 months'; break;
      case 'last2years':   presetName = 'Last 2 years';  break;
      case 'custom':       presetName = 'Custom range';  break;
      default:             presetName = 'Last 30 days';  break;
    }
    // Append the concrete dates so the user always knows exactly what
    // window the analysis covers — defaults to dateRangeBounds, but
    // when "Whole months only" is on we surface the snapped range so
    // the label matches what the chart x-axis actually shows.
    const start = effectiveDateRangeBounds.start;
    const end   = effectiveDateRangeBounds.end;
    if (start && end) {
      return `${presetName} (${formatDateShort(start)} – ${formatDateShort(end)})`;
    }
    return presetName;
  }, [dateRange, effectiveDateRangeBounds]);

  const selectedTypeLabels = useMemo(() => {
    return dataTypes
      .filter(dt => selectedDataTypes.has(dt.id))
      .map(dt => dt.name);
  }, [dataTypes, selectedDataTypes]);


  const facilityFilterLabel = useMemo(() => {
    if (selectedFacilityIds.size === 0) return 'All facilities';
    const savedName = savedFilters.find(f => f.id === parseInt(activeFilterId))?.name;
    if (savedName) return `${savedName} (${selectedFacilityIds.size})`;
    if (groupContext) return `${groupDisplayName} (${selectedFacilityIds.size})`;
    return `${selectedFacilityIds.size} facilit${selectedFacilityIds.size === 1 ? 'y' : 'ies'}`;
  }, [selectedFacilityIds, savedFilters, activeFilterId, groupContext, groupDisplayName]);

  if (loadingFacilities) {
    return (
      <div className="customer-data-page">
        <div className="loading-facilities">
          <div className="loading-spinner"></div>
          <p>Loading cached facilities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-data-page">
      <div className="customer-data-title">
        <h1>
          Customer Data Analysis
          <span className="customer-data-title-group">
            <span className="customer-data-title-sep">—</span>
            <span className="customer-data-title-name">{currentScope.name}</span>
          </span>
        </h1>
        {/* No subtitle here on purpose: facility count lives in the green
            Viewing bar below, date range lives in the filter strip. The
            old subtitle duplicated both. */}
      </div>

      {!groupContext && filterContext && filterContext.error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          Could not load the saved filter (id {filterContext.id}). It may have been deleted.
          <button type="button" className="customer-data-group-clear" onClick={clearFilterContext} style={{ marginLeft: '1rem' }}>
            Dismiss
          </button>
        </div>
      )}

      {showGroupFacilities && (groupContext || filterContext) && (
        <GroupFacilitiesModal
          groupName={groupContext ? groupDisplayName : (filterContext?.name || 'Saved Filter')}
          hierarchy={facilityHierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowGroupFacilities(false)}
        />
      )}

      {facilitiesError && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          <p>{facilitiesError}</p>
        </div>
      )}

      {filtersCollapsed ? (
        <>
          <div className="cd-compact-bar">
            <div className="cd-compact-summary">
              <div className="cd-compact-item">
                <span className="cd-compact-label">Date range</span>
                <span className="cd-compact-value">{dateRangeLabel}</span>
              </div>
              <div className="cd-compact-item">
                <span className="cd-compact-label">Data types</span>
                <span className="cd-compact-value" title={selectedTypeLabels.join(', ')}>
                  {selectedTypeLabels.length === dataTypes.length
                    ? 'All'
                    : selectedTypeLabels.length <= 3
                      ? selectedTypeLabels.join(', ')
                      : `${selectedTypeLabels.slice(0, 2).join(', ')} +${selectedTypeLabels.length - 2}`}
                </span>
              </div>
              {/* Facility scope is shown in the green "Viewing" bar below
                  whenever any scope is active (URL, dropdown, or custom);
                  only surface this column in the truly unscoped case so we
                  aren't repeating the same name twice on the page. */}
              {currentScope.kind === 'none' && (
                <div className="cd-compact-item">
                  <span className="cd-compact-label">Facilities</span>
                  <span className="cd-compact-value">{facilityFilterLabel}</span>
                </div>
              )}
              {selectedSendingFacilityIds.size > 0 && (
                <div className="cd-compact-item">
                  <span className="cd-compact-label">Sending</span>
                  <span
                    className="cd-compact-value"
                    title={Array.from(selectedSendingFacilityIds).map(id => facilityNamesMap.get(id) || `Facility ${id}`).join(', ')}
                  >
                    {selectedSendingFacilityIds.size} facilit{selectedSendingFacilityIds.size === 1 ? 'y' : 'ies'}
                  </span>
                </div>
              )}
              {currentScope.kind !== 'none' && anyDirectional(selectedDataTypes) && (
                <div className="cd-compact-item">
                  <span className="cd-compact-label">Direction</span>
                  <span
                    className="cd-compact-value"
                    title={DIRECTION_OPTIONS.find(o => o.value === direction)?.description || ''}
                  >
                    {DIRECTION_OPTIONS.find(o => o.value === direction)?.short || direction}
                  </span>
                </div>
              )}
              <div className="cd-compact-item">
                <span className="cd-compact-label">Charts</span>
                <span className="cd-compact-value" title="How line charts bucket counts over time">
                  {chartBinningSummary}
                </span>
              </div>
              {dataLoadComplete && (
                <label className={`cd-compact-phi ${showRaw ? 'on' : ''}`} title="When enabled, fetches a sample of up to 1000 raw (un-anonymized) records per data type directly from VitalEngine.">
                  <input
                    type="checkbox"
                    checked={showRaw}
                    onChange={handleRawToggle}
                    disabled={loadingRaw}
                  />
                  <span>Show raw PHI</span>
                  {loadingRaw && rawProgress && (
                    <span className="cd-compact-phi-loading">
                      {rawProgress.current}/{rawProgress.total} · {rawProgress.type}…
                    </span>
                  )}
                </label>
              )}
            </div>
            <div className="cd-compact-actions">
              <button
                type="button"
                className="cd-compact-btn cd-compact-btn-ghost"
                onClick={() => setFiltersCollapsed(false)}
              >
                Edit filters
              </button>
              <button
                type="button"
                className="cd-compact-btn cd-compact-btn-primary"
                onClick={() => loadAllData({ force: true })}
                disabled={isLoadingData || selectedDataTypes.size === 0}
                title="Re-fetch every selected data type from the backend cache, bypassing the in-memory page cache."
              >
                {isLoadingData ? 'Loading…' : 'Reload'}
              </button>
            </div>
          </div>
          {showRaw && (
            <div className="cd-phi-warning">
              <span className="cd-phi-warning-icon">&#9888;</span>
              <span>
                <strong>Raw PHI data is displayed.</strong> Up to 1000 live records per data type, fetched from
                VitalEngine and held only in memory. Drill into individual records to see patient names, DOB, MRN, etc.
              </span>
            </div>
          )}
          {showRaw && rawErrors.length > 0 && (
            <div className="cd-phi-error">
              <span className="cd-phi-warning-icon">&#9888;</span>
              <div>
                <strong>Raw fetch failed for {rawErrors.length === 1 ? rawErrors[0].name : `${rawErrors.length} data types`}.</strong>{' '}
                {rawErrors.length === 1
                  ? rawErrors[0].message
                  : rawErrors.map(e => e.name).join(', ') + '. Anonymized cache is still shown for these.'}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="cd-filters-panel">
          <div className="cd-filters-header">
            <h2>Filters</h2>
            {dataLoadComplete && (
              <button
                type="button"
                className="cd-compact-btn cd-compact-btn-ghost"
                onClick={() => setFiltersCollapsed(true)}
              >
                Collapse
              </button>
            )}
          </div>
          <div className="cd-filters-grid">
            <div className="cd-filter-field">
              <label className="cd-filter-label">Date range</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="date-range-dropdown"
              >
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="last90days">Last 90 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="last9months">Last 9 Months</option>
                <option value="last365days">Last Year</option>
                <option value="last18months">Last 18 Months</option>
                <option value="last2years">Last 2 Years</option>
                <option value="custom">Custom Range</option>
              </select>
              {dateRange === 'custom' && (
                <div className="cd-custom-date-range">
                  <label className="cd-custom-date">
                    <span>From</span>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </label>
                  <label className="cd-custom-date">
                    <span>To</span>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </label>
                </div>
              )}
              <label className="cd-filter-label cd-chart-binning-label">Chart binning</label>
              <select
                value={chartBinningMode}
                onChange={(e) => setChartBinningMode(e.target.value)}
                className="date-range-dropdown cd-chart-binning-select"
                aria-label="Chart time bucket"
              >
                <option value="auto">Auto (by range length)</option>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
              <p className="cd-chart-binning-hint">
                {chartBinningMode === 'auto' ? (
                  <>
                    Selected window is about <strong>{dateRangeSpanDays}</strong> days. Auto currently uses{' '}
                    <strong>{chartBinningPretty}</strong> buckets (≤45 days → Day, 46–100 → Week, longer → Month). Pick
                    Day / Week / Month above to set the default for every chart. Each graph still has its own Group by
                    control if you need a one-off bucket for that series only.
                  </>
                ) : (
                  <>
                    Charts default to <strong>{chartBinningPretty}</strong>; use each chart&apos;s Group by menu to
                    override a single graph.
                  </>
                )}
              </p>
            </div>

            <div className="cd-filter-field cd-filter-field-wide">
              <label className="cd-filter-label">Data types</label>
              <div className="data-type-checkboxes">
                {dataTypes.map(dt => (
                  <label key={dt.id} className="data-type-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDataTypes.has(dt.id)}
                      onChange={() => toggleDataType(dt.id)}
                    />
                    {dt.name}
                  </label>
                ))}
              </div>
            </div>

            <div className="cd-filter-field cd-filter-field-wide">
              <label className="cd-filter-label">Facility filter</label>
              <div className="facility-filter-controls">
                {savedFilters.length > 0 && (
                  <select
                    value={activeFilterId}
                    onChange={(e) => applySavedFilter(e.target.value)}
                    className="date-range-dropdown"
                    aria-label="Saved filter"
                  >
                    <option value="">Saved filter: none</option>
                    {savedFilters.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.name} ({f.facilityIds?.length || 0})
                      </option>
                    ))}
                  </select>
                )}
                <button onClick={() => setShowFacilityFilter(true)} className="select-facilities-btn">
                  Custom Filter
                  {selectedFacilityIds.size > 0 && ` (${selectedFacilityIds.size})`}
                </button>
                {selectedFacilityIds.size > 0 && (
                  <button
                    onClick={() => { setSelectedFacilityIds(new Set()); setActiveFilterId(''); }}
                    className="select-facilities-btn clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              {anyDirectional(selectedDataTypes) && (
                <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <label
                    className="cd-filter-label"
                    style={{ marginBottom: 0 }}
                    title="Inbound = the customer received it. Outbound = the customer sent it. Any = either side. Only applies to Referrals / VitalRounds / Group Referrals - other data types just have one facility per row."
                  >
                    Direction:
                  </label>
                  <DirectionToggle value={direction} onChange={setDirection} />
                </div>
              )}
            </div>

            <div className="cd-filter-field cd-filter-field-wide">
              <label className="cd-filter-label">
                Sending facility filter
                {selectedSendingFacilityIds.size > 0 && (
                  <button
                    type="button"
                    className="cd-sending-clear-all"
                    onClick={() => setSelectedSendingFacilityIds(new Set())}
                  >
                    Clear all ({selectedSendingFacilityIds.size})
                  </button>
                )}
              </label>
              <p className="cd-filter-hint">
                Narrows <strong>Referrals</strong> and <strong>VitalRounds</strong> to rows sent <em>from</em> the selected
                facilities. Messages, Groups, Logins, and Studies aren&apos;t affected because they don&apos;t have a sending side.
              </p>

              {selectedSendingFacilityIds.size > 0 && (
                <div className="cd-sending-chips">
                  {Array.from(selectedSendingFacilityIds).map(id => {
                    const name = facilityNamesMap.get(id) || `Facility ${id}`;
                    return (
                      <span key={id} className="cd-sending-chip" title={`ID ${id}`}>
                        <span className="cd-sending-chip-name">{name}</span>
                        <button
                          type="button"
                          className="cd-sending-chip-remove"
                          aria-label={`Remove ${name}`}
                          title={`Remove ${name}`}
                          onClick={() => removeSendingFacility(id)}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="cd-sending-search-wrap">
                <input
                  type="text"
                  className="cd-sending-search"
                  placeholder="Search facilities by name, city, or state…"
                  value={sendingSearchQuery}
                  onChange={e => setSendingSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setSendingSearchQuery(''); }}
                />
                {sendingSearchQuery.trim() && (
                  <div className="cd-sending-search-results">
                    {sendingSearchMatches.length === 0 ? (
                      <div className="cd-sending-search-empty">No matching facilities.</div>
                    ) : (
                      <ul>
                        {sendingSearchMatches.map(f => (
                          <li key={f.id}>
                            <button
                              type="button"
                              className="cd-sending-search-item"
                              onClick={() => addSendingFacility(f.id)}
                            >
                              <span className="cd-sending-search-name">{f.name}</span>
                              {f.sub && <span className="cd-sending-search-sub">{f.sub}</span>}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="cd-filters-footer">
            <button
              onClick={() => loadAllData()}
              disabled={isLoadingData || selectedDataTypes.size === 0}
              className="load-data-btn"
            >
              {isLoadingData ? 'Loading Cached Data...' : 'Load Data'}
            </button>
            {dataLoadComplete && (
              <label className={`cd-phi-toggle ${showRaw ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={showRaw}
                  onChange={handleRawToggle}
                  disabled={loadingRaw}
                />
                <span className="cd-phi-toggle-text">
                  <strong>Show raw PHI</strong>
                  <em>Fetches up to 1000 live records per data type and reveals patient names, DOB, MRN in drill-downs. Not stored locally.</em>
                </span>
                {loadingRaw && rawProgress && (
                  <span className="cd-compact-phi-loading">
                    {rawProgress.current}/{rawProgress.total} · {rawProgress.type}…
                  </span>
                )}
              </label>
            )}
          </div>
          {showRaw && (
            <div className="cd-phi-warning cd-phi-warning-inline">
              <span className="cd-phi-warning-icon">&#9888;</span>
              <span>
                <strong>Raw PHI data is displayed.</strong> Up to 1000 live records per type, held only in memory.
              </span>
            </div>
          )}
          {showRaw && rawErrors.length > 0 && (
            <div className="cd-phi-error cd-phi-warning-inline">
              <span className="cd-phi-warning-icon">&#9888;</span>
              <div>
                <strong>Raw fetch failed for {rawErrors.length === 1 ? rawErrors[0].name : `${rawErrors.length} data types`}.</strong>{' '}
                {rawErrors.length === 1
                  ? rawErrors[0].message
                  : rawErrors.map(e => e.name).join(', ') + '. Anonymized cache is still shown for these.'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Green "Viewing" bar — anchored just above the analysis results so
          it reads as a header for the data being shown (and so the actions
          are right where the user's eye lands when reviewing). One bar, one
          source of truth (currentScope), regardless of whether the scope
          came from URL, dropdown, or the Custom Filter modal. */}
      {currentScope.kind !== 'none' && (
        <div className={currentScope.bannerClass}>
          <div>
            <strong>{currentScope.viewingLabel}</strong> {currentScope.name}
            <span className="customer-data-group-meta">
              {' '}· {currentScope.facilityCount} facilit{currentScope.facilityCount === 1 ? 'y' : 'ies'}
            </span>
          </div>
          <div className="customer-data-group-actions">
            <button
              type="button"
              className="customer-data-group-view"
              onClick={() => setShowGroupFacilities(true)}
              disabled={!facilityHierarchy?.length || !selectedFacilityIds.size}
              title={currentScope.clearTitle}
            >
              View facilities
            </button>
            <button type="button" className="customer-data-group-clear" onClick={currentScope.clear}>
              {currentScope.clearLabel}
            </button>
          </div>
        </div>
      )}

      {(Object.keys(filteredData).length > 0 || isLoadingData) && (
        <div className="charts-container">
          <div className="cd-results-header">
            <h2>
              Analysis Results
              {showRaw && <span style={{ color: '#c33', fontSize: '0.8em', marginLeft: '0.5rem' }}>(RAW PHI)</span>}
              {!showRaw && dataLoadComplete && <span style={{ color: '#888', fontSize: '0.8em', marginLeft: '0.5rem' }}>(Anonymized)</span>}
            </h2>
            <div className="cd-snap-toggle-wrap">
              <div className="cd-view-toggle" role="group" aria-label="Chart layout">
                <button
                  type="button"
                  className={`cd-view-toggle-btn ${chartViewMode === 'single' ? 'active' : ''}`}
                  onClick={() => changeChartViewMode('single')}
                  aria-pressed={chartViewMode === 'single'}
                  title="One chart; toggle which data types to overlay"
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`cd-view-toggle-btn ${chartViewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => changeChartViewMode('grid')}
                  aria-pressed={chartViewMode === 'grid'}
                  title="Show every data type as its own chart"
                >
                  Grid
                </button>
              </div>
              <label
                className={`cd-snap-toggle ${snapToWholeMonths ? 'is-on' : ''}`}
                title="When on, the chart x-axis is clipped to complete calendar months on each end. Useful for month-over-month comparisons where a half-finished trailing month would distort the visual."
              >
                <input
                  type="checkbox"
                  checked={snapToWholeMonths}
                  onChange={(e) => setSnapToWholeMonths(e.target.checked)}
                />
                <span className="cd-snap-toggle-track" aria-hidden="true">
                  <span className="cd-snap-toggle-thumb" />
                </span>
                <span className="cd-snap-toggle-label">Whole months only</span>
              </label>
              {snapApplied && (
                <span className="cd-snap-hint" title="Range was clipped to whole months">
                  {effectiveDateRangeBounds.start} → {effectiveDateRangeBounds.end}
                </span>
              )}
              {snapEmpty && (
                <span className="cd-snap-warning" title="The selected window contains no full months — showing the original range.">
                  ⚠ No full months in this window
                </span>
              )}
            </div>
          </div>
          {chartViewMode === 'single' ? (
            (() => {
              // Build the overlay series from types that have loaded array
              // data; track which selected types are still loading / errored
              // so we can surface status without breaking the chart.
              const overlaySeries = [];
              let anyLoading = false;
              const erroredNames = [];
              dataTypes.forEach(dt => {
                if (!overlayTypes.has(dt.id)) return;
                const d = filteredData[dt.id];
                if (Array.isArray(d)) {
                  overlaySeries.push({ id: dt.id, records: d });
                } else if (d && d.error) {
                  erroredNames.push(dt.name);
                } else if (loadingTypes.has(dt.id) || (isLoadingData && !d)) {
                  anyLoading = true;
                }
              });

              return (
                <div className="cd-single-view">
                  <div className="cd-single-toolbar">
                    <div className="cd-single-selector" role="group" aria-label="Toggle data types to overlay">
                      {dataTypes.map((dt, i) => {
                        const chartData = filteredData[dt.id];
                        const isError = chartData && chartData.error;
                        const isLoading = loadingTypes.has(dt.id) || (isLoadingData && !chartData);
                        const isOn = overlayTypes.has(dt.id);
                        return (
                          <button
                            key={dt.id}
                            type="button"
                            aria-pressed={isOn}
                            className={`cd-single-pill ${isOn ? 'active' : ''} ${isError ? 'is-error' : ''}`}
                            style={isOn ? { borderColor: getSeriesColor(dt.id, i), boxShadow: `inset 0 0 0 1px ${getSeriesColor(dt.id, i)}` } : undefined}
                            onClick={() => toggleOverlayType(dt.id)}
                          >
                            <span className="cd-single-pill-dot" style={{ background: getSeriesColor(dt.id, i) }} />
                            <span className="cd-single-pill-label">{dt.name}</span>
                            {isLoading && <span className="cd-single-pill-spinner" aria-label="loading" />}
                            {isError && <span className="cd-single-pill-badge" title="Failed to load">!</span>}
                          </button>
                        );
                      })}
                    </div>
                    <div className="cd-single-options">
                      <div className="binning-selector">
                        <label htmlFor="cd-single-binning">Group by:</label>
                        <select
                          id="cd-single-binning"
                          className="binning-dropdown"
                          value={singleBinning}
                          onChange={(e) => setSingleBinningOverride(e.target.value)}
                        >
                          <option value="day">Day</option>
                          <option value="week">Week</option>
                          <option value="month">Month</option>
                        </select>
                      </div>
                      {overlayTypes.has('messages') && (
                        <div className="binning-selector">
                          <label htmlFor="cd-single-msgtype">Message type:</label>
                          <select
                            id="cd-single-msgtype"
                            className="binning-dropdown"
                            value={singleMessageType}
                            onChange={(e) => setSingleMessageType(e.target.value)}
                          >
                            <option value="combined">All messages (combined)</option>
                            {MESSAGE_TYPE_FIELDS.map(mt => (
                              <option key={mt.field} value={mt.field}>{mt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {!compareYoY && (
                        <label className="cd-scale-toggle" title="Shared = one y-axis (compare magnitude). Off = each line auto-scaled to its own range (compare trend shape).">
                          <input
                            type="checkbox"
                            checked={sharedScale}
                            onChange={(e) => changeSharedScale(e.target.checked)}
                          />
                          Shared scale
                        </label>
                      )}
                      <label className="cd-scale-toggle cd-compare-toggle" title="Overlay the same metric for the same window one year earlier. Limited to one data type at a time.">
                        <input
                          type="checkbox"
                          checked={compareYoY}
                          onChange={(e) => toggleCompareYoY(e.target.checked)}
                        />
                        Compare to last year
                      </label>
                    </div>
                  </div>

                  {compareYoY && (
                    <div className="cd-compare-hint">
                      <span>
                        Comparing <strong>{getDataTypeName(Array.from(overlayTypes)[0])}</strong>:{' '}
                        {effectiveDateRangeBounds.start} → {effectiveDateRangeBounds.end}
                        {' '}vs{' '}
                        {priorWindow.start} → {priorWindow.end}
                      </span>
                      {compareLoading && <span className="cd-single-status-loading"><span className="cd-single-pill-spinner" /> Loading last year…</span>}
                      {compareError && <span className="cd-single-status-error">{compareError}</span>}
                      <span className="cd-compare-note">Pick one data type above to compare.</span>
                    </div>
                  )}

                  <div className="cd-single-chart-wrapper">
                    {overlaySeries.length > 0 ? (
                      <CombinedTrendChart
                        seriesData={overlaySeries}
                        binning={singleBinning}
                        windowStart={effectiveDateRangeBounds.start}
                        windowEnd={effectiveDateRangeBounds.end}
                        sharedScale={sharedScale}
                        comparison={compareYoY ? comparisonSeries : null}
                        messageField={singleMessageType === 'combined' ? null : singleMessageType}
                      />
                    ) : anyLoading ? (
                      <div className="chart-placeholder loading">
                        <div className="loading-spinner small"></div>
                        <p>Loading…</p>
                      </div>
                    ) : (
                      <div className="chart-placeholder">
                        <p>Select at least one data type above.</p>
                      </div>
                    )}
                  </div>

                  {(anyLoading || erroredNames.length > 0) && (
                    <div className="cd-single-status">
                      {anyLoading && (
                        <span className="cd-single-status-loading">
                          <span className="cd-single-pill-spinner" /> Some selected types are still loading…
                        </span>
                      )}
                      {erroredNames.length > 0 && (
                        <span className="cd-single-status-error">
                          Failed to load: {erroredNames.join(', ')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="charts-grid">
              {dataTypes.map(dt => {
                if (!selectedDataTypes.has(dt.id)) return null;
                const chartData = filteredData[dt.id];
                const isLoading = loadingTypes.has(dt.id);
                const isWaiting = isLoadingData && !chartData && !isLoading;

                return (
                  <div key={dt.id} className="chart-wrapper">
                    <div className="chart-header">
                      <h3>{dt.name}</h3>
                      {chartData && !chartData.error && (
                        <button
                          className="chart-expand-btn"
                          onClick={() => setExpandedChart(dt.id)}
                          title="Expand"
                        >
                          ⊕
                        </button>
                      )}
                    </div>
                    {isWaiting && (
                      <div className="chart-placeholder waiting"><p>Waiting to load...</p></div>
                    )}
                    {isLoading && (
                      <div className="chart-placeholder loading">
                        <div className="loading-spinner small"></div>
                        <p>Loading {dt.name}...</p>
                      </div>
                    )}
                    {chartData && !chartData.error && (
                      <DataChart
                        data={chartData}
                        dataType={dt.id}
                        chartMode="combined"
                        facilityNamesMap={facilityNamesMap}
                        defaultBinning={effectiveChartBinning}
                        windowStart={effectiveDateRangeBounds.start}
                        windowEnd={effectiveDateRangeBounds.end}
                        facilityGroupByDefault={
                          (dt.id === 'referrals' || dt.id === 'vitalrounds') && selectedSendingFacilityIds.size > 0
                            ? 'sending'
                            : 'receiving'
                        }
                      />
                    )}
                    {chartData && chartData.error && (
                      <div className="chart-placeholder error">
                        <p>Error loading data: {chartData.message}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {expandedChart && filteredData[expandedChart] && !filteredData[expandedChart].error && (
        <div className="chart-modal-overlay" onClick={() => setExpandedChart(null)}>
          <div className="chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <h3>{getDataTypeName(expandedChart)}</h3>
              <button className="chart-modal-close" onClick={() => setExpandedChart(null)} title="Close">✕</button>
            </div>
            <div className="chart-modal-body">
              <DataChart
                data={filteredData[expandedChart]}
                dataType={expandedChart}
                chartMode="combined"
                facilityNamesMap={facilityNamesMap}
                defaultBinning={effectiveChartBinning}
                windowStart={effectiveDateRangeBounds.start}
                windowEnd={effectiveDateRangeBounds.end}
                facilityGroupByDefault={
                  (expandedChart === 'referrals' || expandedChart === 'vitalrounds') && selectedSendingFacilityIds.size > 0
                    ? 'sending'
                    : 'receiving'
                }
              />
            </div>
          </div>
        </div>
      )}

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={facilityHierarchy}
          availableFacilityIds={availableFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => { setSelectedFacilityIds(ids); setActiveFilterId(''); }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </div>
  );
}

export default CustomerData;
